/**
 * Affichage des contacts d’orientation org (annuaire + e-mail Enedis synthétique).
 */

export type ResolvedPerson = {
  email: string | null
  givenName: string | null
  sn: string | null
}

export type DirectionAdminContactRow = {
  nni: string
  dirLib: string
  dirLibLong: string | null
}

export type DirectionAdminGrouped = {
  nni: string
  directionLabels: string[]
}

/**
 * Une entrée par NNI : regroupe les directions où la personne est admin de direction (évite les doublons visuels).
 */
export function groupDirectionAdminsByNni(rows: DirectionAdminContactRow[]): DirectionAdminGrouped[] {
  const byNni = new Map<string, string[]>()
  for (const row of rows) {
    const label =
      row.dirLibLong !== null && row.dirLibLong.trim() !== '' ? row.dirLibLong.trim() : row.dirLib
    const bucket = byNni.get(row.nni)
    if (bucket) {
      if (!bucket.includes(label)) {
        bucket.push(label)
      }
    } else {
      byNni.set(row.nni, [label])
    }
  }
  const grouped: DirectionAdminGrouped[] = []
  for (const [nni, directionLabels] of byNni) {
    directionLabels.sort(function sortDirectionLabelsFr(left, right) {
      return left.localeCompare(right, 'fr')
    })
    grouped.push({ nni, directionLabels })
  }
  grouped.sort(function sortGroupedByNni(left, right) {
    return left.nni.localeCompare(right.nni)
  })
  return grouped
}

function stripDiacritics(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '')
}

/** Partie locale e-mail : minuscules, sans accents ni espaces ni ponctuation parasite. */
function slugifyNamePart(value: string): string {
  return stripDiacritics(value.trim().toLowerCase()).replace(/\s+/g, '').replace(/[^a-z0-9]/g, '')
}

/** E-mails de l’annuaire de dev / recette : ne pas les afficher comme contact métier. */
function isStubOrDevDirectoryEmail(email: string): boolean {
  const lower = email.trim().toLowerCase()
  return (
    lower.includes('@annuaire.mon-orga') ||
    lower.includes('.stub') ||
    lower.includes('@example.') ||
    lower.includes('localhost')
  )
}

/**
 * E-mail de contact du type nom.prenom@enedis.com lorsque nom et prénom sont connus (stub annuaire ignoré côté mailto).
 * Retourne null si un des deux manque après normalisation.
 */
function buildSyntheticEnedisNomPrenomCom(
  givenName: string | null | undefined,
  sn: string | null | undefined
): string | null {
  const givenSlug = slugifyNamePart(givenName ?? '')
  const snSlug = slugifyNamePart(sn ?? '')
  if (givenSlug.length === 0 || snSlug.length === 0) {
    return null
  }
  return `${snSlug}.${givenSlug}@enedis.com`
}

/** Ligne principale : prénom nom, ou NNI si annuaire vide. */
export function contactPrimaryLine(nni: string, resolved: ResolvedPerson | undefined): string {
  if (!resolved) {
    return nni
  }
  const parts = [resolved.givenName, resolved.sn].filter(
    (part): part is string => typeof part === 'string' && part.trim() !== ''
  )
  if (parts.length === 0) {
    return nni
  }
  return parts.join(' ')
}

/** Afficher le NNI en secondaire (on a au moins prénom ou nom depuis l’annuaire). */
export function contactShowNniSecondary(nni: string, resolved: ResolvedPerson | undefined): boolean {
  if (!resolved) {
    return false
  }
  const hasGiven = typeof resolved.givenName === 'string' && resolved.givenName.trim() !== ''
  const hasSn = typeof resolved.sn === 'string' && resolved.sn.trim() !== ''
  return hasGiven || hasSn
}

/** E-mail pour mailto : annuaire réel si présent, sinon synthèse nom.prenom@enedis.com (stubs exclus). */
export function contactMailtoAddress(_nni: string, resolved: ResolvedPerson | undefined): string | null {
  const raw = resolved?.email?.trim()
  if (raw && raw.length > 0 && !isStubOrDevDirectoryEmail(raw)) {
    return raw
  }
  return buildSyntheticEnedisNomPrenomCom(resolved?.givenName, resolved?.sn)
}
